import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { Kpi } from "./Dashboard";
import { fm } from "./lib/analytics";

// Status pill colors
const STATUS = {
  out: { lbl: "نفد", cls: "stk-out" },
  critical: { lbl: "حرج", cls: "stk-critical" },
  low: { lbl: "منخفض", cls: "stk-low" },
  safe: { lbl: "آمن", cls: "stk-safe" },
  no_data: { lbl: "لا يوجد بيع", cls: "stk-nodata" },
};

// v119.248 — «مراقبة المخزون»: waste vs stock join + snapshot history + alerts
function WasteMonitor() {
  const [d, setD] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/inventory/waste-monitor");
        setD(r.data);
      } catch (_e) { setD(null); }
    })();
  }, []);

  if (!d?.ok) return null;
  const t = d.totals || {};
  const hist = [...(d.history?.stk || [])].slice(-8);
  return (
    <div className="card" style={{ marginTop: 16 }} data-testid="waste-monitor">
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>🗑 مراقبة الهدر والمخزون</h3>
        <div style={{ fontSize: 10.5, color: "var(--gy)" }}>
          آخر رفع مخزون: {(d.stock_updated_at || "—").slice(0, 10)} • آخر رفع تالف: {(d.waste_updated_at || "—").slice(0, 10)}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <MiniStat lbl="إجمالي الهدر (كميات)" val={(t.total_waste || 0).toLocaleString("en-US")} color={t.total_waste > 0 ? "#B3261E" : "var(--brown)"} testid="wm-total-waste" />
        <MiniStat lbl="أصناف بها هدر" val={t.wasted_items || 0} color="#9A6A00" testid="wm-wasted-items" />
        <MiniStat lbl="أصناف نفدت" val={t.out_count || 0} color={t.out_count > 0 ? "#B3261E" : "var(--brown)"} testid="wm-out" />
        <MiniStat lbl="أصناف منخفضة" val={t.low_count || 0} color="#9A6A00" testid="wm-low" />
      </div>
      {(d.alerts || []).length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} data-testid="wm-alerts">
          {d.alerts.slice(0, 8).map((a, i) => (
            <span key={i} style={{
              fontSize: 10.5, fontWeight: 700, padding: "4px 10px", borderRadius: 20,
              background: a.type === "waste" ? "#FDECEA" : a.type === "out" ? "#FBE3E3" : "#FFF6E0",
              color: a.type === "low" ? "#7A5C00" : "#8A2A1D",
              border: "1px solid rgba(138,42,29,0.2)",
            }}>
              {a.type === "waste" ? "🗑" : a.type === "out" ? "⛔" : "⚠"} {a.name}: {a.msg}
            </span>
          ))}
        </div>
      )}
      {(d.top_waste || []).length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="t" data-testid="wm-table">
            <thead>
              <tr><th>الصنف</th><th>المتوفّر</th><th>التالف</th><th>نسبة الهدر</th></tr>
            </thead>
            <tbody>
              {d.top_waste.map((r) => (
                <tr key={r.sku || r.name}>
                  <td><b>{r.name}</b></td>
                  <td>{r.qty.toLocaleString("en-US")} <span style={{ color: "var(--gy)", fontSize: 11 }}>{r.unit}</span></td>
                  <td style={{ color: "#B3261E", fontWeight: 700 }}>{r.waste.toLocaleString("en-US")}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 70, height: 6, background: "#F0E6D6", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(100, r.waste_pct)}%`, height: "100%", background: r.waste_pct >= 10 ? "#B3261E" : "#C99A3C" }} />
                      </div>
                      <b style={{ fontSize: 12, color: r.waste_pct >= 10 ? "#B3261E" : "var(--brown)" }}>{r.waste_pct}%</b>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(d.top_waste || []).length === 0 && (
        <div style={{ fontSize: 12.5, color: "var(--gy)" }}>✅ لا يوجد هدر مسجّل في آخر رفع للتالف.</div>
      )}
      {hist.length > 1 && (
        <div style={{ marginTop: 12, fontSize: 11, color: "var(--gy)" }} data-testid="wm-history">
          📅 تتبع الرفعات: {hist.map((h) => `${h.date} (${(h.total_qty || 0).toLocaleString("en-US")})`).join(" ← ")}
        </div>
      )}
    </div>
  );
}

function MiniStat({ lbl, val, color, testid }) {
  return (
    <div data-testid={testid} style={{ background: "var(--bg)", borderRadius: 10, padding: "8px 14px", minWidth: 110, textAlign: "center" }}>
      <div style={{ fontSize: 17, fontWeight: 900, color }}>{val}</div>
      <div style={{ fontSize: 10.5, color: "var(--gy)", marginTop: 2 }}>{lbl}</div>
    </div>
  );
}

export default function Inventory({ stk, onUpload }) {
  const [coverage, setCoverage] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (cov) => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/inventory/analytics?coverage_days=${cov}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "تعذّر تحميل التحليل");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (stk.length > 0) load(coverage);
  }, [coverage, stk.length, load]);

  const items = useMemo(() => {
    const all = data?.items || [];
    let f = all;
    if (filter !== "all") f = f.filter((x) => x.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      f = f.filter((x) => (x.name || "").toLowerCase().includes(q) || (x.id || "").toLowerCase().includes(q));
    }
    return f;
  }, [data, filter, search]);

  const exportPurchaseList = () => {
    const rows = data?.purchase_suggestions || [];
    if (!rows.length) return;
    const csv = [
      ["المعرف", "الاسم", "الكمية الحالية", "الوحدة", "الاستهلاك اليومي", "أيام التغطية", "الكمية المقترح شراؤها", "القيمة اليومية المقدّرة"].join(","),
      ...rows.map((r) => [
        r.id, `"${(r.name || "").replace(/"/g, "'")}"`, r.qty, r.unit,
        r.daily_qty, r.days_cover ?? "", r.suggest_qty, r.daily_value,
      ].join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `قائمة_شراء_${coverage}يوم_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!stk.length) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 30 }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🏭</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>ارفع ملف "مستوى المخزون" لرؤية التحليل</div>
        <p style={{ fontSize: 12.5, color: "var(--gy)", marginBottom: 14, lineHeight: 1.8 }}>
          يدعم تقرير المخزون من رتم — نحلّل الأصناف، نحسب أيام التغطية، نتوقع الاستهلاك الشهري، ونقترح قائمة شراء جاهزة.
        </p>
        <button data-testid="upload-stock-btn" className="btn btn-primary" onClick={onUpload}>📤 رفع ملف المخزون</button>
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div data-testid="inventory-tab">
      <Kpi
        items={[
          { l: "إجمالي الأصناف", v: s.total_items?.toLocaleString("en-US") || "0" },
          { l: "نفد المخزون", v: (s.out_of_stock || 0).toLocaleString("en-US"), c: s.out_of_stock > 0 ? "r" : "" },
          { l: "يحتاج إعادة طلب", v: (s.low_stock || 0).toLocaleString("en-US"), c: s.low_stock > 0 ? "o" : "" },
          { l: "أصناف آمنة", v: (s.safe_stock || 0).toLocaleString("en-US"), c: "g" },
          { l: "صرف شهري متوقع", v: fm(s.monthly_consumption_value || 0) + " ر.س", c: "b" },
          { l: "مدة التغطية المستهدفة", v: `${s.coverage_days_target || coverage} يوم` },
        ]}
      />

      <WasteMonitor />

      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>⚙ تخصيص التحليل</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>أيام التغطية المستهدفة:</label>
            <input
              data-testid="coverage-slider"
              type="range" min="7" max="90" step="1"
              value={coverage}
              onChange={(e) => setCoverage(parseInt(e.target.value, 10))}
              style={{ width: 140 }}
            />
            <span style={{ fontSize: 14, fontWeight: 800, color: "var(--gd)", minWidth: 50 }}>{coverage} يوم</span>
            <button data-testid="btn-export-purchase" className="btn btn-success" onClick={exportPurchaseList} disabled={!data}>📥 تصدير قائمة الشراء</button>
          </div>
        </div>
        {loading && <div style={{ fontSize: 13, color: "var(--gy)" }}>⏳ جارٍ التحليل...</div>}
        {err && <div style={{ fontSize: 13, color: "var(--rd)" }}>{err}</div>}
      </div>

      {data?.top_low_stock?.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16 }}>🚨 أصناف تحتاج طلب فوري</h3>
          <div style={{ overflowX: "auto" }}>
            <table className="t" data-testid="top-low-stock-table">
              <thead>
                <tr>
                  <th>الصنف</th>
                  <th>المتوفّر</th>
                  <th>الاستهلاك اليومي</th>
                  <th>أيام التغطية</th>
                  <th>الحالة</th>
                  <th>الكمية المقترحة</th>
                </tr>
              </thead>
              <tbody>
                {data.top_low_stock.map((r) => (
                  <tr key={r.id || r.name}>
                    <td><b>{r.name}</b><div style={{ fontSize: 10.5, color: "var(--gy)" }}>{r.id}</div></td>
                    <td>{r.qty.toLocaleString("en-US")} <span style={{ color: "var(--gy)", fontSize: 11 }}>{r.unit}</span></td>
                    <td>{r.daily_qty.toLocaleString("en-US")}</td>
                    <td>{r.days_cover === null ? "—" : `${r.days_cover} يوم`}</td>
                    <td><span className={`stk-pill ${STATUS[r.status]?.cls}`}>{STATUS[r.status]?.lbl || r.status}</span></td>
                    <td><b style={{ color: "var(--gd)" }}>{r.suggest_qty.toLocaleString("en-US")}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12, justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>📋 قائمة المخزون الكاملة ({items.length})</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              data-testid="inv-search"
              placeholder="🔍 بحث بالاسم أو المعرف"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: "8px 12px", border: "1.5px solid var(--bg)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", minWidth: 200 }}
            />
            <select data-testid="inv-filter" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: "1.5px solid var(--bg)", fontSize: 13 }}>
              <option value="all">كل الحالات</option>
              <option value="out">نفد</option>
              <option value="critical">حرج</option>
              <option value="low">منخفض</option>
              <option value="safe">آمن</option>
              <option value="no_data">بدون بيع</option>
            </select>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="t">
            <thead>
              <tr>
                <th>الصنف</th>
                <th>المتوفّر</th>
                <th>الاستهلاك اليومي</th>
                <th>أيام التغطية</th>
                <th>التوقع الشهري</th>
                <th>قيمة الصرف اليومي</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id || r.name}>
                  <td><b>{r.name}</b><div style={{ fontSize: 10.5, color: "var(--gy)" }}>{r.id}</div></td>
                  <td>{r.qty.toLocaleString("en-US")} <span style={{ color: "var(--gy)", fontSize: 11 }}>{r.unit}</span></td>
                  <td>{r.daily_qty.toLocaleString("en-US")}</td>
                  <td>{r.days_cover === null ? "—" : `${r.days_cover}`}</td>
                  <td>{r.monthly_forecast.toLocaleString("en-US")}</td>
                  <td>{r.daily_value > 0 ? `${fm(r.daily_value)} ر.س` : "—"}</td>
                  <td><span className={`stk-pill ${STATUS[r.status]?.cls}`}>{STATUS[r.status]?.lbl || r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
